import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import useTitle from 'hooks/useTitle'
import apiService from 'Service'
import LoadingSpinner from 'Components/LoadingSpinner'
import { formatCivilDate, formatUtcDateTime } from 'Utilities/dateTime'

const FALLBACK_METHODS = [
    { id: 'Transferencia', name: 'Transferencia', code: 'Transferencia', discountPercent: 0, gatewayProvider: 'None', checkoutType: 'manual' },
    { id: 'PagoMovil', name: 'PagoMovil', code: 'PagoMovil', discountPercent: 0, gatewayProvider: 'None', checkoutType: 'manual' },
]

function normalizeMethod(m) {
    if (typeof m === 'string') {
        return {
            id: m,
            name: m,
            code: m,
            discountPercent: 0,
            gatewayProvider: 'None',
            requiresReference: true,
            currencyCode: 'USD',
        }
    }

    const id = m.id ?? m.Id ?? m.code ?? m.Code ?? m.name ?? m.Name
    const name = m.name ?? m.Name ?? m.code ?? m.Code ?? (id != null ? String(id) : '')
    const code = m.code ?? m.Code ?? name
    return {
        id: id ?? code,
        name,
        code,
        discountPercent: m.discountPercent ?? m.DiscountPercent ?? 0,
        gatewayProvider: m.gatewayProvider ?? m.GatewayProvider ?? 'None',
        instructions: m.instructions ?? m.Instructions,
        requiresReference: m.requiresReference ?? m.RequiresReference ?? true,
        currencyCode: m.currencyCode ?? m.CurrencyCode ?? 'USD',
    }
}

export default function LicensePaymentPage() {
    const { t } = useTranslation()
    useTitle(t('license_payment_title'))

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [status, setStatus] = useState(null)
    const [payments, setPayments] = useState([])
    const [methods, setMethods] = useState(FALLBACK_METHODS)
    const [checkoutInfo, setCheckoutInfo] = useState(null)
    const [message, setMessage] = useState(null)
    const [error, setError] = useState(null)
    const [form, setForm] = useState({
        amount: '',
        currency: 'USD',
        paymentMethod: 'Transferencia',
        paymentMethodId: null,
        reference: '',
        destinationAccount: '',
        notes: '',
        receipt: null,
    })

    const load = () => {
        setLoading(true)
        Promise.all([
            apiService.Licensing.status(),
            apiService.Licensing.payments().catch(() => []),
            apiService.Licensing.paymentMethods().catch(() => []),
        ])
            .then(([st, pays, catalog]) => {
                setStatus(st)
                setPayments(Array.isArray(pays) ? pays : [])
                const list = (Array.isArray(catalog) ? catalog.map(normalizeMethod) : FALLBACK_METHODS)
                    .filter(m => m.id != null && m.name)
                const methodsToUse = list.length > 0 ? list : FALLBACK_METHODS
                setMethods(methodsToUse)
                setForm(f => ({
                    ...f,
                    paymentMethod: methodsToUse[0].name,
                    paymentMethodId: methodsToUse[0].id,
                    currency: methodsToUse[0].currencyCode ?? 'USD',
                }))
            })
            .catch(() => setError(t('error_loading_data')))
            .finally(() => setLoading(false))
    }

    useEffect(() => { load() }, [])

    const selectedMethod = methods.find(m =>
        (form.paymentMethodId && m.id === form.paymentMethodId) ||
        m.name === form.paymentMethod ||
        m.code === form.paymentMethod
    )

    const onMethodChange = (value) => {
        const method = methods.find(m => String(m.id) === value || m.name === value || m.code === value)
        setCheckoutInfo(null)
        setForm({
            ...form,
            paymentMethod: method?.name ?? value,
            paymentMethodId: method?.id ?? null,
            currency: method?.currencyCode ?? form.currency,
        })
    }

    const onInitiateGateway = async () => {
        if (!selectedMethod?.id) {
            setError(t('error_saving_data'))
            return
        }
        setSubmitting(true)
        setError(null)
        setMessage(null)
        try {
            const returnUrl = `${window.location.origin}${window.location.pathname}?payment=return`
            const cancelUrl = `${window.location.origin}${window.location.pathname}?payment=cancel`
            const result = await apiService.Licensing.initiatePayment(selectedMethod.id, returnUrl, cancelUrl)
            setCheckoutInfo(result)

            if (result.redirectUrl || result.RedirectUrl) {
                window.location.href = result.redirectUrl || result.RedirectUrl
                return
            }

            setForm(f => ({
                ...f,
                amount: String(result.amount ?? result.Amount ?? f.amount),
                reference: result.gatewayReference || result.GatewayReference || result.merchantTradeNo || result.MerchantTradeNo || f.reference,
                destinationAccount: result.walletAddress || result.WalletAddress || result.merchantTradeNo || result.MerchantTradeNo || f.destinationAccount,
            }))
            setMessage(t('license_payment_checkout_ready'))
        } catch {
            setError(t('error_saving_data'))
        } finally {
            setSubmitting(false)
        }
    }

    const onSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setError(null)
        setMessage(null)
        try {
            await apiService.Licensing.submitPayment({
                ...form,
                paymentMethod: selectedMethod?.code ?? selectedMethod?.name ?? form.paymentMethod,
            })
            setMessage(t('license_payment_submitted'))
            setCheckoutInfo(null)
            setForm(f => ({
                ...f,
                amount: '',
                reference: '',
                destinationAccount: '',
                notes: '',
                receipt: null,
            }))
            load()
        } catch {
            setError(t('error_saving_data'))
        } finally {
            setSubmitting(false)
        }
    }

    const isGateway = selectedMethod && selectedMethod.gatewayProvider && selectedMethod.gatewayProvider !== 'None'

    if (loading) return <LoadingSpinner />

    return (
        <div>
            <h4>{t('license_payment_title')}</h4>
            <p className="text-muted">{t('license_payment_hint')}</p>

            {status && (
                <Alert variant={status.RemainingDays <= 7 ? 'warning' : 'info'}>
                    {t('days_left')}: <strong>{status.RemainingDays}</strong>
                    {' — '}
                    {t('license_expires_on')}: {status.ExpirationDate ? formatCivilDate(status.ExpirationDate) : '-'}
                </Alert>
            )}

            {message && <Alert variant="success">{message}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="mb-4">
                <Card.Header>{t('report_license_payment')}</Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('payment_method')}</Form.Label>
                                    <Form.Control
                                        as="select"
                                        className="license-payment-method-select"
                                        value={String(form.paymentMethodId ?? form.paymentMethod ?? '')}
                                        onChange={(e) => onMethodChange(e.target.value)}
                                    >
                                        {methods.map(m => (
                                            <option key={String(m.id)} value={String(m.id)}>
                                                {m.name}
                                                {m.discountPercent > 0 ? ` (-${m.discountPercent}%)` : ''}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    {selectedMethod?.instructions && (
                                        <Form.Text className="text-muted d-block mt-1">{selectedMethod.instructions}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('amount')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        value={form.amount}
                                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    />
                                    {selectedMethod?.discountPercent > 0 && form.amount && (
                                        <Form.Text className="text-success">
                                            {t('discount_applied', { percent: selectedMethod.discountPercent })}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('reference')}</Form.Label>
                                    <Form.Control
                                        required
                                        value={form.reference}
                                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                        placeholder={checkoutInfo?.gatewayReference ? String(checkoutInfo.gatewayReference) : undefined}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {checkoutInfo && (checkoutInfo.qrContent || checkoutInfo.QrContent) && (
                            <Alert variant="info">
                                <strong>{t('scan_to_pay')}</strong>
                                <div className="small mt-1">{checkoutInfo.qrContent || checkoutInfo.QrContent}</div>
                            </Alert>
                        )}

                        {checkoutInfo && (checkoutInfo.walletAddress || checkoutInfo.WalletAddress) && (
                            <Alert variant="info">
                                <strong>{checkoutInfo.network || checkoutInfo.Network} — {checkoutInfo.tokenSymbol || checkoutInfo.TokenSymbol}</strong>
                                <div>{checkoutInfo.walletAddress || checkoutInfo.WalletAddress}</div>
                                <div>{t('amount')}: {checkoutInfo.amount ?? checkoutInfo.Amount} {checkoutInfo.currencyCode ?? checkoutInfo.CurrencyCode}</div>
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>{t('destination_account')}</Form.Label>
                            <Form.Control
                                required
                                placeholder={t('destination_account_placeholder')}
                                value={form.destinationAccount}
                                onChange={(e) => setForm({ ...form, destinationAccount: e.target.value })}
                            />
                            <Form.Text className="text-muted">{t('destination_account_hint')}</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('payment_receipt')}</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => setForm({ ...form, receipt: e.target.files?.[0] || null })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('notes')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </Form.Group>
                        <div className="d-flex gap-2 flex-wrap">
                            {isGateway && (
                                <Button type="button" variant="outline-primary" disabled={submitting} onClick={onInitiateGateway}>
                                    {submitting ? t('saving') : t('start_gateway_payment')}
                                </Button>
                            )}
                            <Button type="submit" disabled={submitting}>
                                {submitting ? t('saving') : t('submit_payment')}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>{t('payment_history')}</Card.Header>
                <Card.Body>
                    {payments.length === 0 ? (
                        <p className="text-muted mb-0">{t('no_payments_yet')}</p>
                    ) : (
                        <Table responsive size="sm">
                            <thead>
                                <tr>
                                    <th>{t('date')}</th>
                                    <th>{t('amount')}</th>
                                    <th>{t('payment_method')}</th>
                                    <th>{t('reference')}</th>
                                    <th>{t('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map(p => (
                                    <tr key={p.Id || p.id}>
                                        <td>{formatUtcDateTime(p.CreatedAt || p.createdAt)}</td>
                                        <td>{p.Amount ?? p.amount} {p.Currency ?? p.currency}</td>
                                        <td>{p.PaymentMethod ?? p.paymentMethod}</td>
                                        <td>{p.ExternalReference ?? p.externalReference}</td>
                                        <td>{(p.IsValidated ?? p.isValidated) ? t('validated') : t('pending')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </div>
    )
}
